import React from "react";
import PropTypes from "prop-types";
import { Badge, Button, ButtonGroup, Form, Input } from "reactstrap";
import { cameraShape } from "../../shared/propShapes";
import { isTemp, MAX_VIEWS_PER_CAMERA, activeViewCount } from "../treeReducer";
import AlertChannelRouting from "./AlertChannelRouting";
import CameraMissedThefts from "./CameraMissedThefts";
import FieldRow, { Section } from "./FieldRow";
import ForeignKeySelect from "./ForeignKeySelect";
import InferenceMachineAutocomplete from "./InferenceMachineAutocomplete";

function toNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export default function CameraForm({ camera, foreignKeys, dispatch, canCreate }) {
  const update = (patch) => dispatch({ type: "UPDATE_CAMERA", publicId: camera.public_id, patch });
  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    update({ [field]: value });
  };
  const setNum = (field) => (e) => update({ [field]: toNumber(e.target.value) });
  const setFk = (field) => (newValue) => update({ [field]: newValue });

  const onRevert = () => dispatch({ type: "REVERT_ENTITY", publicId: camera.public_id });
  const onDelete = () => dispatch({ type: "DELETE_CAMERA", publicId: camera.public_id });
  const onDuplicate = () => dispatch({ type: "DUPLICATE_CAMERA", publicId: camera.public_id });
  const onAddView = () => dispatch({ type: "ADD_VIEW", cameraId: camera.public_id, partial: {} });

  const viewsFull = activeViewCount(camera.views) >= MAX_VIEWS_PER_CAMERA;

  return (
    <Form>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0">
            📷 Camera {camera.slot_number}
            {isTemp(camera.public_id) && (
              <Badge color="info" className="ml-2" style={{ fontSize: "0.65rem" }}>
                new
              </Badge>
            )}
            {camera.localDeleted && (
              <Badge color="danger" className="ml-2" style={{ fontSize: "0.65rem" }}>
                marked for deletion
              </Badge>
            )}
          </h6>
          <code style={{ fontSize: "0.75rem", color: "#6c757d" }}>{camera.public_id}</code>
        </div>
        <Button size="sm" outline color="secondary" onClick={onRevert}>
          ↺ Revert
        </Button>
      </div>

      <Section title="Camera & feed">
        <FieldRow label="Camera number" hint="Unique within the capture device">
          <Input type="number" bsSize="sm" value={camera.slot_number ?? ""} onChange={setNum("slot_number")} />
        </FieldRow>
        <FieldRow label="Stream quality">
          <Input
            type="select"
            bsSize="sm"
            className="custom-select"
            value={camera.stream_quality}
            onChange={set("stream_quality")}
          >
            <option>MAIN</option>
            <option>SUB</option>
          </Input>
        </FieldRow>
        <FieldRow label="RTSP URL suffix">
          <Input
            bsSize="sm"
            value={camera.rtsp_url_suffix || ""}
            onChange={set("rtsp_url_suffix")}
            placeholder={camera.url_mode === "EXPLICIT" ? "required" : "optional"}
          />
        </FieldRow>
        <FieldRow label="Protocol">
          <Input type="select" bsSize="sm" className="custom-select" value={camera.protocol} onChange={set("protocol")}>
            <option>TCP</option>
            <option>UDP</option>
          </Input>
        </FieldRow>
      </Section>

      <Section title="Inference routing">
        <FieldRow label="Inference machine">
          <InferenceMachineAutocomplete value={camera.inference_machine} onChange={setFk("inference_machine")} />
        </FieldRow>
        <FieldRow label="Depth of field">
          <ForeignKeySelect
            value={camera.depth_of_field}
            options={foreignKeys.depthOfFields}
            onChange={setFk("depth_of_field")}
          />
        </FieldRow>
      </Section>

      <Section title="Alert routing">
        <AlertChannelRouting
          camera={camera}
          alertChannels={foreignKeys.alertChannels}
          segments={foreignKeys.segments}
          onChange={setFk("alert_channel")}
        />
      </Section>

      <Section title="State">
        <FieldRow label="Active">
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <Input type="checkbox" checked={!!camera.is_active} onChange={set("is_active")} />
            <span>{camera.is_active ? "on" : "off"}</span>
          </div>
        </FieldRow>
      </Section>

      <Section title="Missed thefts">
        <FieldRow label="Missed thefts" hint="Saved immediately — independent of Save All">
          {/* key on public_id remounts the modal on every camera switch, resetting its
              per-camera state (drafts, save token, in-flight save) synchronously — closing
              the render-to-effect window where the new camera could pair with old drafts. */}
          <CameraMissedThefts key={camera.public_id} camera={camera} />
        </FieldRow>
      </Section>

      <div className="d-flex justify-content-between mt-3">
        {canCreate && (
          <ButtonGroup size="sm">
            <Button
              color="success"
              onClick={onAddView}
              disabled={camera.localDeleted || viewsFull}
              title={viewsFull ? `A camera can have at most ${MAX_VIEWS_PER_CAMERA} views` : undefined}
            >
              + Add view
            </Button>
            <Button outline color="secondary" onClick={onDuplicate} disabled={camera.localDeleted}>
              Duplicate camera
            </Button>
          </ButtonGroup>
        )}
        <Button size="sm" outline color="danger" onClick={onDelete} disabled={camera.localDeleted}>
          {camera.localDeleted ? "Marked for deletion" : "Delete camera"}
        </Button>
      </div>
    </Form>
  );
}

CameraForm.propTypes = {
  /** Whether the user may create entities — the `editorRole` gate carried by the legacy recorders. */
  canCreate: PropTypes.bool.isRequired,
  camera: cameraShape.isRequired,
  foreignKeys: PropTypes.shape({
    alertChannels: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        is_enabled: PropTypes.bool,
        is_allowed_for_channel: PropTypes.bool,
        is_production: PropTypes.bool,
        is_simulation: PropTypes.bool,
        segment: PropTypes.string,
      })
    ).isRequired,
    depthOfFields: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    segments: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, key: PropTypes.string, name: PropTypes.string })
    ).isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
};
